import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router-dom'
import { Box, Typography, Button, Paper, useTheme, alpha } from '@mui/material'
import { Home as HomeIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material'
import { ROUTES } from '@/utils/constants'
import '@/styles/error-page.css'

export type ErrorPageStatusCode = 403 | 404 | 500

export interface ErrorPageProps {
  /** Код ответа: 403, 404, 500 или другой (показывается как «Ошибка») */
  statusCode?: number
  /** Дополнительное сообщение от API (показывается под заголовком) */
  message?: string | null
  /** Кнопка «Назад»: текст и ссылка. Если не задано — показывается «На главную» */
  backTo?: { label: string; to: string } | null
  /** Рендер внутри контента (например, DashboardLayout) или полноэкранный центрированный блок */
  fullPage?: boolean
}

const STATUS_CONFIG: Record<number, { titleKey: string }> = {
  403: { titleKey: 'errorPage.403.title' },
  404: { titleKey: 'errorPage.404.title' },
  500: { titleKey: 'errorPage.500.title' },
}

function getConfig(statusCode: number) {
  return (
    STATUS_CONFIG[statusCode] ?? {
      titleKey: 'errorPage.generic.title',
    }
  )
}

export function ErrorPage({
  statusCode = 500,
  message = null,
  backTo = null,
  fullPage = false,
}: ErrorPageProps) {
  const { t } = useTranslation()
  const theme = useTheme()
  const config = getConfig(statusCode)

  const backLabel = backTo?.label ?? t('errorPage.backToHome')
  const backToPath = backTo?.to ?? ROUTES.DASHBOARD

  const cardContent = (
    <Paper elevation={0} className="error-page-card" sx={{ borderRadius: 3 }}>
      <Typography
          component="p"
          fontWeight={700}
          gutterBottom
          sx={{
            fontSize: { xs: '4rem', sm: '6rem', md: '8rem' },
            lineHeight: 1,
            color: 'text.primary',
            letterSpacing: '-0.02em',
          }}
        >
          {statusCode}
        </Typography>
        <Typography variant="h5" component="h1" fontWeight={600} sx={{ mb: 2, color: 'text.primary' }}>
          {t(config.titleKey)}
        </Typography>
        {message && (
          <Typography
            variant="body2"
            sx={{
              mb: 2,
              px: 2,
              py: 1,
              borderRadius: 1,
              bgcolor: alpha(theme.palette.error.main, 0.06),
              color: 'text.secondary',
              fontStyle: 'italic',
              maxWidth: 420,
              mx: 'auto',
            }}
          >
            {message}
          </Typography>
        )}
        <Button
          component={RouterLink}
          to={backToPath}
          variant="contained"
          startIcon={backTo ? <ArrowBackIcon /> : <HomeIcon />}
          sx={{ mt: 1 }}
        >
          {backLabel}
        </Button>
    </Paper>
  )

  if (fullPage) {
    return (
      <Box className="error-page-wrapper error-page-wrapper--full" sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Box sx={{ width: '100%', maxWidth: 520 }}>{cardContent}</Box>
      </Box>
    )
  }

  return (
    <Box
      className="error-page-wrapper error-page-wrapper--fill-main"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 'calc(100vh - 0px)',
        py: 4,
        px: 2,
      }}
    >
      {cardContent}
    </Box>
  )
}
